import { EventType } from "@/analytics/analytics-events";
import { SmartNavigationEventProperties } from "@/analytics/camera-menu-events";
import { useSmartNavigationStore } from "@/components/r3f/controls/smart-navigation/smart-navigation-store";
import { useIsOnDraftPage } from "@/hooks/use-is-on-draft-page";
import { useLocalStorage } from "@faro-lotv/app-component-toolbox";
import {
  FaroTooltip,
  SmartNavigationIcon,
  ToolButton,
} from "@faro-lotv/flat-ui";
import { Analytics } from "@faro-lotv/foreign-observers";
import { useCallback, useEffect, type JSX } from "react";

/**
 * @returns A camera mode button to toggle the SCENE-like smart navigation in the 3D view,
 *  or null when the SmartNavigation feature is not enabled
 *
 * While the mouse is in the 3D view the button icon hints at the interaction under
 * the pointer: look-around inside the smart navigation circle, orbit outside of it.
 */
export function SmartNavigationToggle(): JSX.Element | null {
  const isSmartNavigationEnabled = useSmartNavigationStore(
    (state) => state.isSmartNavigationEnabled,
  );
  const setIsSmartNavigationEnabled = useSmartNavigationStore(
    (state) => state.setIsSmartNavigationEnabled,
  );

  // 'isSmartNavigationEnabled' is only used to initialize the local storage state, if no previous status exists in local storage.
  const [smartNavigationOn, setSmartNavigationOn] = useLocalStorage(
    "smartNavigationOn",
    isSmartNavigationEnabled,
  );

  useEffect(() => {
    if (isSmartNavigationEnabled !== smartNavigationOn) {
      setIsSmartNavigationEnabled(smartNavigationOn);
    }
  }, [
    isSmartNavigationEnabled,
    smartNavigationOn,
    setIsSmartNavigationEnabled,
  ]);

  const view = useIsOnDraftPage() ? "draft" : "live";

  const toggleSmartNavigation = useCallback(() => {
    Analytics.track<SmartNavigationEventProperties>(
      smartNavigationOn
        ? EventType.disableSmartNavigation
        : EventType.enableSmartNavigation,
      { view },
    );
    setSmartNavigationOn(!smartNavigationOn);
  }, [smartNavigationOn, view, setSmartNavigationOn]);

  return (
    <FaroTooltip
      title="Smart navigation: drag inside the circle to look around, outside to orbit"
      placement="right"
    >
      <ToolButton
        selected={smartNavigationOn}
        onClick={toggleSmartNavigation}
        aria-label="toggle smart navigation"
      >
        <SmartNavigationIcon />
      </ToolButton>
    </FaroTooltip>
  );
}
